import React from 'react';
import BaseProps from "../../../types/BaseProps";
import {FacetModel, FacetsList, FilterSelection} from "../../../types/Search";
import { AggregationType } from '../../../types/AggregationType';

import BaseFacet from "./BaseFacet";
import TypeFacet from './type-facet/TypeFacet';
import DateRangeFacet from './date-range/DateRangeFacet';
import KeywordFacet from './keyword/KeywordFacet';
import NumericRangeFacet from './numeric-range/NumericRangeFacet';

import "./Facets.scss";

type FacetsProps = BaseProps & {
  // list with facet models
  facets?: FacetsList;
  // event which must be passed to each facet implementation
  onSelect: (selected: FilterSelection) => void;
  onDeselect: (deselected: string) => void;
};

/**
 * Each facet component implementation must implement this API.
 */
 export type FacetProps = {
  // contains items which should be represented in the facet
  model: FacetModel;
  // event which should be triggered when selection is being made in the facet
  onSelect: (selection: FilterSelection) => void;
  onDeselect: (deselected: string) => void;
}

/**
 * Component which implements a list with facet implementations.
 */
class Facets extends React.Component<FacetsProps> {

  calculateFacetTotal = (facet: FacetModel) => {
    let facetTotal = 0;
    Object.keys(facet.data).forEach((filterKey: string) => {
      facetTotal += facet.data[filterKey].count;
    });
    return facetTotal;
  };

  render() {
    // iterate facets model
    // resolve facet type
    // pass concrete facet component implementation to Facet
    const facets: FacetsList = this.props.facets || [];
    return (
      <div className="Facets">
        {
          facets.map((facet: FacetModel) => {
            const FacetComponent = resolveFacetComponent(facet);
            return <BaseFacet key={facet.title}
                              type={facet.facetType}
                              title={facet.title}
                              hasSelection={facet.hasSelection}
                              totalCount={this.calculateFacetTotal(facet)}>
              <FacetComponent model={facet} onSelect={this.props.onSelect} onDeselect={this.props.onDeselect}/>
            </BaseFacet>
          })
        }
      </div>
    );
  }
}

/**
 * Finds out proper facet component implementation that can handle given facet type.
 *
 * @param {AggregationType} facetType
 * @returns {React.ElementType}
 */
function resolveFacetComponent(facet: FacetModel): React.ElementType {
  const facetType: AggregationType = facet.facetType;
  if (facet.field === "type") {
    return TypeFacet;
  } else if (facetType === AggregationType.DATERANGE) {
    return DateRangeFacet;
  } else if (facetType === AggregationType.KEYWORD) {
    return KeywordFacet;
  } else if (facetType === AggregationType.NUMERIC_RANGE) {
    return NumericRangeFacet;
  }
  throw Error(`Unsupported aggregation type ${facetType}`);
}

export default Facets;
